// Keeps this phone's NFC pickup pass ready (Android): while the app is open, sign the pass for the
// next 10 minutes of 30 s windows and hand them to the card service, which answers a reader with
// the current one. Nothing is stored: the service holds them in memory, and a phone left closed
// longer than that says "open Vavr" when tapped. The key never leaves the WebView.
import { useEffect } from "react";
import { windowOf, WINDOW_SECONDS } from "@vavr/core";
import { api } from "../shared/api.ts";
import type { AppPlatform } from "../shared/platform.ts";
import type { Kv } from "../shared/kv.ts";
import { passFor, qrText } from "./pass.ts";
import type { Overview } from "./status.ts";

/** How many windows ahead to sign (20 x 30 s = 10 minutes), and how often to top them up. */
export const NFC_WINDOWS = 20;
const REFRESH_MS = 4 * 60_000;

/** The windows to sign from `nowMs`: the current one and the next NFC_WINDOWS - 1. */
export const nfcWindows = (nowMs: number, n = NFC_WINDOWS) => {
  const w = windowOf(Math.floor(nowMs / 1000));
  return Array.from({ length: n }, (_, i) => w + i);
};

export function useNfcPass(platform: AppPlatform, kv: Kv, overview: Overview | null) {
  const siteId = overview?.sites.find((s) => s.children.length)?.site.id ?? null;
  useEffect(() => {
    const nfc = platform.nfcPass;
    if (!nfc || !siteId) return;
    let live = true;
    const load = async () => {
      const st = await nfc.status();
      if (!live || !st.supported) return;
      const { cached } = await passFor(kv, siteId, (site, holder) => api("POST", `/api/v1/guardian/sites/${site}/pass`, { holder_key: holder }), Date.now(), false);
      if (!live || !cached) return;
      const passes = [];
      for (const w of nfcWindows(Date.now())) passes.push({ window: w, text: await qrText(kv, cached, w * WINDOW_SECONDS * 1000) });
      if (live) await nfc.setPasses(passes);
    };
    void load().catch(() => undefined);
    const t = setInterval(() => void load().catch(() => undefined), REFRESH_MS);
    const wake = () => { if (document.visibilityState === "visible") void load().catch(() => undefined); };
    document.addEventListener("visibilitychange", wake);
    return () => { live = false; clearInterval(t); document.removeEventListener("visibilitychange", wake); };
  }, [platform, kv, siteId]);
}
